import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Dropdown, Input, Option, Tag, TagGroup } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, CreateSector, Sector, Sede, UpdateSector } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { useOfficesFormStyles } from '../Offices.Styles';
import {
  SectorFormValues,
  buildCreateSectorPayload,
  buildSectorUpdatePayload,
  findById,
  getAssignableRooms,
  sectorToFormValues,
  validateSectorForm,
} from '../Offices.Functions';
import { ConsultorioStatusPill, ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../../theme/components/GuideField';

export interface SectorDrawerProps {
  open: boolean;
  sector?: Sector;
  defaultSedeId?: number;
  sedes: Sede[];
  sectores: Sector[];
  consultorios: Consultorio[];
  isSaving: boolean;
  saveError?: ErrorType;
  onCreate: (payload: CreateSector) => void;
  onUpdate: (id: number, payload: UpdateSector) => void;
  onClose: () => void;
}

const SectorDrawer = ({
  open,
  sector,
  defaultSedeId,
  sedes,
  sectores,
  consultorios,
  isSaving,
  saveError,
  onCreate,
  onUpdate,
  onClose,
}: SectorDrawerProps) => {
  const { t } = useTranslation();
  const styles = useOfficesFormStyles();
  const editing = sector?.id !== undefined;
  const [values, setValues] = useState<SectorFormValues>(sectorToFormValues(sector, consultorios, defaultSedeId));
  const [showErrors, setShowErrors] = useState(false);
  const [roomSearch, setRoomSearch] = useState('');

  useEffect(() => {
    if (open) {
      setValues(sectorToFormValues(sector, consultorios, defaultSedeId));
      setShowErrors(false);
      setRoomSearch('');
    }
    // Sólo al abrir / cambiar de sector (B11).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sector?.id, defaultSedeId]);

  const errors = validateSectorForm(values, sectores, sector?.id);
  const sedeName = findById(sedes, values.branchId)?.name || '';

  const assignable = useMemo(() => {
    const needle = roomSearch.trim().toLowerCase();
    return getAssignableRooms(consultorios, sector?.id).filter(
      (c) => !needle || (c.name || '').toLowerCase().includes(needle) || (c.description || '').toLowerCase().includes(needle)
    );
  }, [consultorios, sector?.id, roomSearch]);

  const selectedRooms = values.consultorioIds
    .map((id) => findById(consultorios, id) || ({ id, name: `#${id}` } as Consultorio))
    .filter(Boolean);

  const toggleRoom = (id: number, checked: boolean) =>
    setValues((v) => ({
      ...v,
      consultorioIds: checked ? Array.from(new Set([...v.consultorioIds, id])) : v.consultorioIds.filter((x) => x !== id),
    }));

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    if (editing && sector?.id !== undefined) {
      onUpdate(sector.id, buildSectorUpdatePayload(sector, consultorios, { name: values.name, consultorioIds: values.consultorioIds }));
    } else {
      onCreate(buildCreateSectorPayload(values));
    }
  };

  return (
    <GuideDrawer
      open={open}
      size="medium"
      title={editing ? t('Offices.sector.editTitle') : t('Offices.sector.createTitle')}
      onDismiss={onClose}
      preventDismiss={isSaving}
      closeLabel={t('Offices.actions.close')}
      testId="sector-drawer"
      secondaryAction={{ label: t('Offices.actions.cancel') }}
      primaryAction={{ label: isSaving ? t('Offices.actions.saving') : t('Offices.actions.save'), onClick: () => submit(), loading: isSaving }}
    >
      <GuideDrawerForm onSubmit={submit}>
        {saveError && <ErrorBanner title={t('Offices.messages.saveError')} error={saveError} testId="sector-save-error" />}
        <GuideField
          label={t('Offices.sector.sede')}
          required
          hint={editing ? t('Offices.sector.sedeLockedHint') : undefined}
          error={showErrors && errors.branchId ? t(errors.branchId) : undefined}
        >
          {editing ? (
            <Input value={sedeName} readOnly aria-readonly />
          ) : (
            <Dropdown
              placeholder={t('Offices.sector.sedePlaceholder')}
              value={sedeName}
              selectedOptions={values.branchId !== undefined ? [String(values.branchId)] : []}
              onOptionSelect={(_, d) => setValues((v) => ({ ...v, branchId: d.optionValue ? Number(d.optionValue) : undefined }))}
              data-testid="sector-sede"
            >
              {sedes.map((s) => (
                <Option key={s.id} value={String(s.id)} text={s.name || ''}>
                  {s.name}
                </Option>
              ))}
            </Dropdown>
          )}
        </GuideField>
        <GuideFieldGrid data-testid="sector-identity">
          <GuideField label={t('Offices.sector.name')} required error={showErrors && errors.name ? t(errors.name) : undefined}>
            <Input
              value={values.name}
              maxLength={150}
              placeholder={t('Offices.sector.namePlaceholder')}
              onChange={(_, d) => setValues((v) => ({ ...v, name: d.value }))}
            />
          </GuideField>
          {!editing && (
            <GuideField
              label={t('Offices.sector.code')}
              required
              hint={t('Offices.sector.codeHint')}
              error={showErrors && errors.number ? t(errors.number) : undefined}
            >
              <Input
                value={values.number}
                maxLength={20}
                placeholder={t('Offices.sector.codePlaceholder')}
                onChange={(_, d) => setValues((v) => ({ ...v, number: d.value }))}
              />
            </GuideField>
          )}
        </GuideFieldGrid>

        <GuideDrawerSection title={t('Offices.sector.roomsTitle')} description={t('Offices.sector.roomsHint')}>
          {selectedRooms.length > 0 ? (
            <TagGroup
              aria-label={t('Offices.sector.selectedRooms')}
              onDismiss={(_, data) => toggleRoom(Number(data.value), false)}
              data-testid="sector-selected-rooms"
            >
              {selectedRooms.map((room) => (
                <Tag key={room.id} value={String(room.id)} dismissible dismissIcon={{ 'aria-label': t('Offices.sector.removeRoom', { name: room.name }) }}>
                  {room.name}
                </Tag>
              ))}
            </TagGroup>
          ) : (
            <span className={styles.muted}>{t('Offices.sector.noRoomsSelected')}</span>
          )}

          <GuideField label={t('Offices.sector.searchRooms')}>
            <Input
              contentBefore={<Search20Regular />}
              value={roomSearch}
              placeholder={t('Offices.search')}
              onChange={(_, d) => setRoomSearch(d.value)}
            />
          </GuideField>
          <div className={styles.roomList} role="group" aria-label={t('Offices.sector.roomsTitle')}>
            {assignable.length === 0 && <span className={styles.muted}>{t('Offices.sector.noAssignableRooms')}</span>}
            {assignable.map((room) => (
              <div key={room.id} className={styles.roomOption}>
                <Checkbox
                  label={room.name || `#${room.id}`}
                  checked={values.consultorioIds.includes(room.id as number)}
                  onChange={(_, d) => toggleRoom(room.id as number, Boolean(d.checked))}
                />
                <ConsultorioStatusPill consultorio={room} />
              </div>
            ))}
          </div>
        </GuideDrawerSection>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default SectorDrawer;
